/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useCallback } from "react";
import {
  TextField,
  Checkbox,
  DropZone,
  BlockStack,
  Text,
  Button,
  Thumbnail,
  Box,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { MultiComboOption, MultiSelectCombobox } from "@shared/ui";
import { DatePickerField } from "@shared/ui";
import { TranslateLink } from "@shared/ui";
import { PhoneNumberField } from "@shared/ui";
import { SingleSelectCombobox } from "@shared/ui";
import { PatientJsonBadge, Renderer } from "@features/clinic-patients/clinic/types";
import { PriceField } from "@shared/ui";
import { JSONBadgeRenderer } from "@shared/ui";
/* same props interface */
interface ComboOption {
  label: string;
  value: string;
}

interface EditPropertiesRendererProps {
  id:
    | "textInput"
    | "textArea"
    | "number"
    | "checkbox"
    | "phoneNumber"
    | "email"
    | "date"
    | "combobox"
    | "combeBoxMultiple"
    | "dropZone"
    | "jsonBadge";
  name?: string;
  label?: string;
  value?: string | number | boolean | string[] | PatientJsonBadge;
  onChange: (newValue: any) => void;
  disabled?: boolean;
  helpText?: string;
  placeholder?: string;
  comboOptions?: ComboOption[];
  error?: string;
  isRequired?: boolean;
  file?: File;
  imageUrl?: string;
  onDrop?: (_dropFiles: File[], acceptedFiles: File[]) => void;
  onRemoveFile?: () => void;
  renderer?: Renderer;
  patientId?: string;
}

function EditPropertiesRenderer({
  isRequired,
  id,
  label,
  value,
  onChange,
  disabled = false,
  helpText,
  placeholder,
  comboOptions = [],
  error,
  file,
  imageUrl,
  onDrop,
  onRemoveFile,
  renderer,
  patientId,
}: EditPropertiesRendererProps) {
  const { t } = useTranslation();

  // Common label/placeholder
  const stringValue = value == null ? "" : String(value);
  const computedLabel = label ?? t("PropertyTypeRenderer.label");

  const computedPlaceholder =
    placeholder ?? t("PropertyTypeRenderer.placeholder");

  // Checkbox
  const boolValue = value === "true" ? true : false;

  // Multi-Select
  const multiSelected = useMemo(() => {
    if (id !== "combeBoxMultiple") return [];
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === "string" && value.length > 0) {
      return value.split(",").map((v) => v.trim());
    }
    return [];
  }, [id, value]);

  const handleMultiSelectChange = useCallback(
    (newSelected: string[]) => {
      onChange(newSelected);
    },
    [onChange]
  );

  // Phone number example: parse string as JSON object
  if (id === "phoneNumber") {
    return (
      <PhoneNumberField
        value={stringValue}
        onChange={(newE164) => onChange(newE164)}
        phoneLabel={computedLabel}
        phoneHelpText={helpText}
        phoneError={error}
        disabled={disabled}
        isRequired={isRequired}
      />
    );
  } else if (renderer?.properties?.currency === true) {
    return (
      <PriceField
        label={computedLabel}
        value={stringValue}
        onChange={onChange}
        placeholder={computedPlaceholder}
        error={error}
        requiredIndicator={isRequired}
        helpText={helpText}
        disabled={disabled}
      />
    );
  }

  switch (id) {
    case "textInput":
    case "textArea":
    case "email":
    case "number":
      return (
        <TextField
          label={computedLabel}
          value={stringValue}
          onChange={onChange}
          onBlur={() => onChange(stringValue)}
          type={id === "number" ? "number" : "text"}
          multiline={id === "textArea" ? 4 : undefined}
          disabled={disabled}
          helpText={helpText}
          placeholder={computedPlaceholder}
          autoComplete="off"
          error={error}
          requiredIndicator={isRequired}
          clearButton
          onClearButtonClick={() => onChange("")}
        />
      );

    case "checkbox":
      return (
        <Checkbox
          label={computedLabel}
          checked={boolValue}
          onChange={(value) => onChange(`${value}`)}
          disabled={disabled}
          helpText={helpText}
          error={error}
        />
      );
    case "jsonBadge":
      return (
        <JSONBadgeRenderer
          label={computedLabel}
          badge={value as PatientJsonBadge}
          patientId={patientId ?? ""}
          allowRefresh={true}
        />
      );

    case "date":
      return (
        <DatePickerField
          value={stringValue}
          onChange={onChange}
          label={computedLabel || t("PropertyTypeRenderer.dateLabel")}
          disabled={disabled}
          isRequired={isRequired}
        />
      );

    case "combobox":
      return (
        <SingleSelectCombobox
          label={computedLabel}
          options={comboOptions}
          onChange={onChange}
          value={stringValue}
          disabled={disabled}
          helpText={helpText}
          error={error}
        />
      );

    case "combeBoxMultiple":
      return (
        <BlockStack gap="200">
          <Text variant="headingSm" as="h4">
            {computedLabel}
          </Text>
          <MultiSelectCombobox
            options={comboOptions as MultiComboOption[]}
            selected={multiSelected}
            onChange={handleMultiSelectChange}
            label={computedLabel}
            placeholder={computedPlaceholder}
          />
          {error && (
            <Text tone="critical" as="p">
              {error}
            </Text>
          )}
        </BlockStack>
      );

    case "dropZone":
      return (
        <BlockStack gap="200">
          <Text as="h3" variant="bodyMd">
            {computedLabel}
          </Text>
          {file || imageUrl ? (
            <BlockStack>
              <Thumbnail
                source={file ? URL.createObjectURL(file) : imageUrl || ""}
                alt={file?.name || "Current image"}
                size="large"
              />
              {file && <Text as="p">{file.name}</Text>}
              <Box>
                <Button
                  variant="tertiary"
                  onClick={onRemoveFile}
                  tone="critical"
                >
                  {t("common.remove")}
                </Button>
              </Box>
            </BlockStack>
          ) : (
            <DropZone
              onDrop={onDrop}
              disabled={disabled}
              allowMultiple={false}
              type="image"
              accept="image/*"
            >
              <DropZone.FileUpload
                actionTitle={
                  computedPlaceholder || t("PropertyTypeRenderer.uploadFiles")
                }
              />
            </DropZone>
          )}
          {error && (
            <Text tone="critical" as="p">
              {error}
            </Text>
          )}
          {helpText && <TranslateLink text={helpText} />}
        </BlockStack>
      );

    default:
      return (
        <TextField
          autoComplete="off"
          label={computedLabel || t("PropertyTypeRenderer.unknownTypeLabel")}
          value={stringValue}
          onChange={onChange}
          onBlur={() => onChange(stringValue)}
          disabled={disabled}
          helpText={helpText}
          error={error}
          clearButton
          onClearButtonClick={() => onChange("")}
        />
      );
  }
}

export default EditPropertiesRenderer;
